// src/features/asset/assetSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../api/apiClient';

export const fetchAssets = createAsyncThunk(
  'asset/fetchAssets',
  /*async ({ nextPageToken = null, limit = 10 }) => {
    console.log('fetchAssets:', { nextPageToken, limit });
    const response = await apiClient.get('/portfolios/{{portfolioId}}/assets', {
      params: { limit, nextPageToken },
    });
    return response.data;
  }*/
);

// Async thunk to fetch a single asset by ID
export const fetchAssetById = createAsyncThunk(
  'asset/fetchAssetById',
  async (assetId, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/assets/${assetId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

// Async thunk to delete an asset by ID
export const deleteAssetById = createAsyncThunk(
  'asset/deleteAssetById',
  async (assetId, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/assets/${assetId}`);
      return assetId; // Return the asset ID for potential state updates
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

// Async thunk to add a new asset
export const addAsset = createAsyncThunk(
  'asset/addAsset',
  async (assetData, { rejectWithValue }) => {
    try {
      console.log('assetData:', assetData);
      const response = await apiClient.post('/assets', assetData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateAsset = createAsyncThunk(
  'asset/updateAsset',
  async ({ id, ...data }, thunkAPI) => {
    try {
      const response = await apiClient.put(`/assets/${id}`, data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to update asset');
    }
  }
);

// Create asset slice with separate statuses for fetching and adding
const assetSlice = createSlice({
  name: 'asset',
  initialState: {
    assets: [],
    total: 0,
    pageTokens: [], // Stack for navigating backwards
    nextPageToken: null, // Stores token for next page
    fetchStatus: 'idle', // Status for fetching assets
    fetchByIdStatus: 'idle', // Status for fetching a single asset
    addStatus: 'idle', // Status for adding an asset
    error: null,
  },
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    resetAddStatus: (state) => {
      state.addStatus = 'idle';
      state.error = null;
    },
    resetFetchStatus: (state) => {
      state.fetchStatus = 'idle';
      state.error = null;
    },
    goToPreviousPage: (state) => {
      if (state.pageTokens.length > 1) {
        state.pageTokens.pop(); // Remove last token
        state.nextPageToken = state.pageTokens[state.pageTokens.length - 1] || null;
      }
    },
    resetFetchByIdStatus: (state) => {
      state.fetchByIdStatus = 'idle';
      state.error = null;
    },
    resetDeleteStatus: (state) => {
      state.deleteStatus = 'idle';
      state.error = null;
    },
    resetUpdateStatus: (state) => {
      state.updateStatus = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchAssets lifecycle
      .addCase(fetchAssets.pending, (state) => {
        state.fetchStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchAssets.fulfilled, (state, action) => {
        state.fetchStatus = 'succeeded';
        state.assets = action.payload.assets;
        state.total = action.payload.total;

        // Store pagination tokens
        if (state.nextPageToken) {
          state.pageTokens.push(state.nextPageToken);
        }
        state.nextPageToken = action.payload.nextPageToken || null;
      })
      .addCase(fetchAssets.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.error = action.error.message || 'Failed to fetch assets';
      })

      .addCase(fetchAssetById.pending, (state) => {
        state.fetchByIdStatus = 'loading';
        state.singleAsset = null;
        state.error = null;
      })
      .addCase(fetchAssetById.fulfilled, (state, action) => {
        state.fetchByIdStatus = 'succeeded';
        state.singleAsset = action.payload;
      })
      .addCase(fetchAssetById.rejected, (state, action) => {
        state.fetchByIdStatus = 'failed';
        state.error = action.payload.error || 'Failed to fetch asset';
      })

      // Handle deleteAssetById lifecycle
      .addCase(deleteAssetById.pending, (state) => {
        state.deleteStatus = 'loading';
        state.error = null;
      })
      .addCase(deleteAssetById.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.assets = state.assets.filter(
          (asset) => asset._id !== action.payload,
        );
      })
      .addCase(deleteAssetById.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.error = action.payload.error || 'Failed to delete asset';
      })

      // Handle addAsset lifecycle
      .addCase(addAsset.pending, (state) => {
        state.addStatus = 'loading';
        state.error = null;
      })
      .addCase(addAsset.fulfilled, (state, action) => {
        state.addStatus = 'succeeded';
    
        // ✅ Add the new asset at the beginning of the list (optional)
        state.assets.unshift(action.payload);
    
        // ✅ Fetch updated assets (ensure list is always fresh)
        state.fetchStatus = 'idle'; // Reset fetch status to trigger refetch
      })
      .addCase(addAsset.rejected, (state, action) => {
        state.addStatus = 'failed';
        state.error = action.payload.error || 'Failed to add asset';
      })

      .addCase(updateAsset.pending, (state) => {
        state.updateStatus = 'loading';
        state.error = null;
      })
      .addCase(updateAsset.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        const updatedAsset = action.payload;
        state.assets = state.assets.map((asset) =>
          asset._id === updatedAsset._id ? updatedAsset : asset
        );
      })
      .addCase(updateAsset.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.error = action.payload.error || 'Failed to update asset';
      });
  },
});

export const {
  setPage,
  resetAddStatus,
  resetFetchStatus,
  resetFetchByIdStatus,
  resetDeleteStatus,
  resetUpdateStatus,
  goToPreviousPage,
} = assetSlice.actions;
export default assetSlice.reducer;
